import { useEffect, useState, useCallback, useMemo } from "react";
import {
  GraduationCap, Search, RefreshCw, Trophy, CalendarCheck2,
  Handshake, Sparkles, Quote, ListChecks, UserRound, Store, Clock, Phone, Info,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CallRecordingPlayer } from "@/components/CallRecordingPlayer";

function fmtDur(s) {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}min ${String(sec).padStart(2, "0")}s`;
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function initials(name) {
  return (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

const OUTCOME = {
  accord: { label: "Accord obtenu", bg: "rgba(31,157,85,0.14)", fg: "#0F5A2F", Icon: Handshake },
  rendez_vous: { label: "Rendez-vous", bg: "rgba(31,157,85,0.14)", fg: "#0F5A2F", Icon: CalendarCheck2 },
};
const SENTIMENT = {
  positif: { label: "Positif", bg: "rgba(31,157,85,0.12)", fg: "#0F5A2F" },
  neutre: { label: "Neutre", bg: "var(--surface-2)", fg: "rgba(24,24,24,0.66)" },
  "négatif": { label: "Négatif", bg: "rgba(214,69,69,0.12)", fg: "#B23434" },
};

function Chip({ bg, fg, children, Icon }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold"
      style={{ background: bg, color: fg }}>
      {Icon && <Icon className="h-3 w-3" />} {children}
    </span>
  );
}

function StatTile({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[var(--border-soft)] bg-white p-3.5 shadow-[var(--shadow-sm)]">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"><Icon className="h-5 w-5" /></span>
      <div className="min-w-0">
        <div className="text-2xl font-extrabold tabular-nums text-ink" data-testid={`formation-stat-${label}`}>{value}</div>
        <div className="truncate text-[12px] font-semibold text-muted-ink">{label}</div>
      </div>
    </div>
  );
}

// Carte d'un appel à succès : interlocuteur/magasin, insights IA et lecteur audio.
function SuccessCallCard({ call }) {
  const a = call.ai_analysis || {};
  const oc = OUTCOME[(a.outcome || "").toLowerCase()] || OUTCOME.accord;
  const sent = a.sentiment ? (SENTIMENT[a.sentiment] || SENTIMENT.neutre) : null;
  const score = a.score != null ? Math.round(Number(a.score)) : null;
  const keyPoints = Array.isArray(a.key_points) ? a.key_points.filter(Boolean).slice(0, 4) : [];
  const verbatims = Array.isArray(a.verbatims) ? a.verbatims.filter(Boolean).slice(0, 2) : [];
  const summary = call.ai_summary || a.summary || null;
  const title = call.spot_name || call.contact_name || call.to_number || "Appel";
  const dur = call.recording_duration || call.duration_sec || 0;
  const fileLabel = `${call.cdp_name || "cdp"}-${title}`;

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]"
      data-testid={`formation-card-${call.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[rgba(238,114,46,0.14)] text-sm font-black text-[var(--shariti-orange)]">
            {initials(call.cdp_name)}
          </span>
          <div className="min-w-0">
            <div className="flex items-start gap-1.5 text-sm font-bold text-ink">
              <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-ink" />
              <span className="break-words" data-testid={`formation-cdp-${call.id}`}>{call.cdp_name || "CDP"}</span>
            </div>
            <div className="mt-0.5 flex items-start gap-1.5 text-[12px] text-muted-ink">
              <Store className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="break-words">{title}</span>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Chip bg={oc.bg} fg={oc.fg} Icon={oc.Icon}>{oc.label}</Chip>
          {score != null && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--shariti-orange)]">
              <Trophy className="h-3 w-3" /> Score {score}/100
            </span>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-ink">
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDateTime(call.started_at)}</span>
        <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{fmtDur(dur)}</span>
        {sent && <Chip bg={sent.bg} fg={sent.fg}>{sent.label}</Chip>}
        {a.interlocuteur && <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{a.interlocuteur}</span>}
      </div>

      {summary && (
        <p className="mt-2.5 line-clamp-2 text-[13px] leading-relaxed text-ink/80">{summary}</p>
      )}

      {keyPoints.length > 0 && (
        <div className="mt-2.5 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-1)] p-2.5">
          <div className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
            <ListChecks className="h-3 w-3" /> Ce qui a marché
          </div>
          <ul className="space-y-0.5">
            {keyPoints.map((k, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[12px] text-ink/85">
                <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {verbatims.length > 0 && (
        <div className="mt-2 space-y-1">
          {verbatims.map((v, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-lg bg-[rgba(238,114,46,0.06)] px-2.5 py-1.5 text-[12px] italic text-ink/80">
              <Quote className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
              <span>{v}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 border-t border-[var(--border-soft)] pt-3">
        <CallRecordingPlayer callId={call.id} totalSec={dur} fileLabel={fileLabel} />
      </div>
    </div>
  );
}

export default function Formation() {
  const [calls, setCalls] = useState([]);
  const [cdps, setCdps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cdp, setCdp] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calls/training-library", { params: { limit: 300 } });
      setCalls(Array.isArray(data?.calls) ? data.calls : []);
      setCdps(Array.isArray(data?.cdps) ? data.cdps : []);
      setTotal(data?.total || 0);
    } catch {
      setCalls([]); setCdps([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = calls;
    if (cdp !== "all") list = list.filter((c) => c.cdp_id === cdp);
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((c) => [c.spot_name, c.contact_name, c.to_number, c.cdp_name,
        c.ai_summary, (c.ai_analysis || {}).interlocuteur]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
    }
    return list;
  }, [calls, cdp, q]);

  return (
    <>
      <PageHeader
        title="Formation — Appels à succès"
        subtitle="Écoutez les appels « closés » (accords et rendez-vous décrochés) des CDP expérimentés pour vous entraîner et progresser."
        action={
          <Button variant="outline" onClick={load} disabled={loading}
            className="border-[var(--border-soft)] bg-white" data-testid="formation-refresh">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        }
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative max-w-xs flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (magasin, interlocuteur…)"
              className="bg-white pl-9" data-testid="formation-search" />
          </div>
          <Select value={cdp} onValueChange={setCdp}>
            <SelectTrigger className="w-full bg-white sm:w-64" data-testid="formation-cdp-filter">
              <SelectValue placeholder="Tous les CDP" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les CDP</SelectItem>
              {cdps.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      <PageBody>
        {/* Encart pédagogique */}
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[var(--border-soft)] bg-[#FFF7F1] px-4 py-3.5" data-testid="formation-intro">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[var(--shariti-orange)]"><GraduationCap className="h-5 w-5" /></span>
          <div className="text-[13px] leading-relaxed text-ink/80">
            <span className="font-bold text-[#8A3A10]">Bibliothèque de formation.</span>{" "}
            Chaque appel ci-dessous a abouti à un <b>accord</b> ou un <b>rendez-vous</b>. Écoutez l'enregistrement,
            repérez les bonnes formulations et le traitement des objections — c'est le meilleur entraînement pour
            un nouveau CDP. Les appels sont classés par pertinence (score) puis par date.
          </div>
        </div>

        {/* Stats */}
        {!loading && total > 0 && (
          <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile Icon={Trophy} label="Appels à succès" value={total} />
            <StatTile Icon={UserRound} label="CDP contributeurs" value={cdps.length} />
            <StatTile Icon={GraduationCap} label="Affichés" value={filtered.length} />
          </div>
        )}

        {/* Contenu */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-soft)] bg-white p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-11 w-11 rounded-full" />
                  <div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-2/3" /></div>
                </div>
                <Skeleton className="mt-3 h-16 w-full rounded-xl" />
                <Skeleton className="mt-3 h-9 w-full rounded-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white px-6 py-16 text-center" data-testid="formation-empty">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[rgba(238,114,46,0.1)] text-[var(--shariti-orange)]"><GraduationCap className="h-7 w-7" /></span>
            <h3 className="mt-4 text-lg font-bold text-ink">
              {total === 0 ? "Aucun appel à succès enregistré pour l'instant" : "Aucun résultat pour ce filtre"}
            </h3>
            <p className="mt-1.5 max-w-md text-sm text-muted-ink">
              {total === 0
                ? "Dès qu'un CDP décroche un accord ou un rendez-vous lors d'un appel enregistré, il apparaîtra ici automatiquement pour servir d'exemple de formation."
                : "Essayez d'élargir votre recherche ou de choisir un autre CDP."}
            </p>
            {total > 0 && (
              <Button variant="outline" className="mt-4 border-[var(--border-soft)] bg-white"
                onClick={() => { setCdp("all"); setQ(""); }} data-testid="formation-clear-filters">
                Réinitialiser les filtres
              </Button>
            )}
            {total === 0 && (
              <div className="mt-4 flex items-center gap-1.5 rounded-lg bg-[var(--surface-1)] px-3 py-2 text-[12px] text-muted-ink">
                <Info className="h-3.5 w-3.5 shrink-0" /> Astuce : l'enregistrement des appels doit être activé (Administration → Téléphonie).
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="formation-list">
            {filtered.map((c) => <SuccessCallCard key={c.id} call={c} />)}
          </div>
        )}
      </PageBody>
    </>
  );
}
